import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { queryKeys } from 'core/api/queryKeys';
import { humanizeApiError } from 'core/errors/mapFieldErrors';
import { useI18n } from 'core/i18n/I18nProvider';
import { paths } from 'core/routing/paths';
import { AssignedSpecialistCard } from 'features/consultations/AssignedSpecialistCard';
import { canRequestConsultation, isActiveConsultationStatus, publicConsultationView } from 'features/consultations/consultationUtils';
import { userApi } from 'features/home/home.api';
import { EmptyState, ErrorState, Skeleton, StatusChip } from 'shared/components/Feedback';
import { PageMeta } from 'shared/components/PageMeta';
import { Pagination } from 'shared/components/Pagination';
import { formatDateTime, formatDateTimeInTimeZone } from 'shared/utilities/format';

export function ConsultationsPage() {
  const { t, locale } = useI18n();
  const [page, setPage] = useState(1);
  const homeQuery = useQuery({
    queryKey: queryKeys.home,
    queryFn: () => userApi.home(),
    refetchOnWindowFocus: 'always',
    refetchOnReconnect: true,
  });
  const query = useQuery({
    queryKey: queryKeys.consultations(page),
    queryFn: () => userApi.consultations({ page, per_page: 15 }),
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });

  if (query.isLoading || homeQuery.isLoading) {
    return <Skeleton rows={6} />;
  }
  if (query.isError) {
    return <ErrorState title={humanizeApiError(query.error, t)} onRetry={() => query.refetch()} retryLabel={t('actions.retry')} />;
  }

  const items = (query.data?.items || []).map(publicConsultationView);
  const home = homeQuery.data || {};
  const activeConsultation = isActiveConsultationStatus(home.consultation?.status) ? home.consultation : null;
  const allowRequest = canRequestConsultation(home);

  return (
    <div className="list-page">
      <PageMeta titleKey="meta.consultations" />
      <header className="page-header">
        <div>
          <h1>{t('consultations.title')}</h1>
          <p className="muted">{t('consultations.assignHint')}</p>
        </div>
        {allowRequest ? (
          <Link className="btn btn-primary" to={paths.consultationNew}>{t('actions.requestConsultation')}</Link>
        ) : activeConsultation?.id ? (
          <Link className="btn btn-secondary" to={paths.consultation(activeConsultation.id)}>{t('actions.viewConsultation')}</Link>
        ) : null}
      </header>
      {!allowRequest && !activeConsultation ? (
        <p className="muted" data-testid="consultations-needs-result">{t('consultations.needsResult')}</p>
      ) : null}
      {activeConsultation ? (
        <p className="muted" data-testid="consultations-active-hint">{t('consultations.activeHint')}</p>
      ) : null}
      {items.length === 0 ? <EmptyState title={t('consultations.empty')} /> : (
        <div className="record-table">
          <div className="record-table-head">
            <span>{t('consultations.referenceLabel')}</span>
            <span>{t('consultations.goal')}</span>
            <span>{t('consultations.specialist')}</span>
            <span>{t('consultations.scheduled')}</span>
            <span />
          </div>
          {items.map((item) => {
            const scheduled = formatDateTimeInTimeZone(item.scheduled_at, locale, item.display_timezone);
            return (
              <article key={item.id} className="record-row">
                <div>
                  <strong>{item.reference}</strong>
                  <StatusChip tone={item.status}>{item.status_label || t(`consultations.statuses.${item.status}`)}</StatusChip>
                </div>
                <p>{item.goal_label || item.goal}</p>
                <AssignedSpecialistCard consultation={item} compact />
                <p className="muted">
                  {item.scheduled_at
                    ? `${scheduled.datetime}${scheduled.timeZoneLabel ? ` ${scheduled.timeZoneLabel}` : ''}`
                    : formatDateTime(item.created_at, locale)}
                </p>
                <Link className="btn btn-secondary" to={paths.consultation(item.id)}>{t('actions.view')}</Link>
              </article>
            );
          })}
        </div>
      )}
      <Pagination meta={query.data?.meta} page={page} onPageChange={setPage} />
    </div>
  );
}
